import { reactive } from "vue";
import { getHandwritingRecognizer } from "@/services/handwriting/TrocrRecognizer";
import { HANDWRITING_TOOL_ENABLED } from "@/services/handwriting/featureFlag";

/**
 * Adoption state of the handwriting (draw2text) feature on this device.
 *
 * - "new": never proposed or reset from settings — show the discovery
 *   button with its "new" badge.
 * - "installed": the user activated the feature and the model loaded
 *   successfully — show the compact tool button, warm up at app boot.
 * - "declined": the user asked not to be offered the feature again.
 * - "incompatible": activation failed on this device (memory, browser…).
 *
 * Deliberately independent from the model version: shipping a new model
 * (DR_HANDWRITING_MODEL change) must NOT re-trigger the onboarding.
 */
export type HandwritingFeatureStatus =
  | "new"
  | "installed"
  | "declined"
  | "incompatible";

const STORAGE_KEY = "dr:handwriting-feature";
/** Pre-onboarding flag (kept for migration from earlier builds). */
const LEGACY_USED_KEY = "dr:handwriting-used";
/** Set once the incompatibility alert has been sent, to avoid spamming. */
const ALERTED_KEY = "dr:handwriting-alerted";

function readInitialStatus(): HandwritingFeatureStatus {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (
      stored === "installed" ||
      stored === "declined" ||
      stored === "incompatible"
    ) {
      return stored;
    }
    // Migration: users of the pre-onboarding builds already use the tool.
    if (localStorage.getItem(LEGACY_USED_KEY) === "1") {
      localStorage.setItem(STORAGE_KEY, "installed");
      localStorage.removeItem(LEGACY_USED_KEY);
      return "installed";
    }
  } catch {
    // storage unavailable: behave as a fresh device
  }
  return "new";
}

const state = reactive({
  status: readInitialStatus() as HandwritingFeatureStatus,

  setStatus(status: HandwritingFeatureStatus) {
    this.status = status;
    try {
      localStorage.setItem(STORAGE_KEY, status);
    } catch {
      // storage unavailable: state will simply not persist
    }
  },

  /** Settings action: offer the feature again on this device. */
  reset() {
    this.setStatus("new");
    try {
      localStorage.removeItem(ALERTED_KEY);
    } catch {
      // ignore
    }
  },

  /** Whether the incompatibility alert was already sent from this device. */
  wasAlerted(): boolean {
    try {
      return localStorage.getItem(ALERTED_KEY) === "1";
    } catch {
      return false;
    }
  },

  markAlerted() {
    try {
      localStorage.setItem(ALERTED_KEY, "1");
    } catch {
      // ignore
    }
  },
});

export default state;

/**
 * Handwriting bookkeeping at app boot.
 *
 * When the tool is on, the recognizer is warmed up BEFORE any drawing (and
 * its DrawingWeb heap) is loaded: on memory-constrained devices the model
 * cannot allocate its buffers once the CAD engine has fragmented the
 * process address space, so reserving early is what makes the two coexist.
 * Only for devices where the feature is installed — everyone else pays
 * nothing.
 *
 * When the tool is off, the opposite: devices that had installed it keep
 * ~66 MB of model files in Cache Storage for a feature they can no longer
 * reach, so the cache is released. It is rebuilt from the server if the
 * feature ever comes back.
 */
export function prepareHandwritingAtBoot(): void {
  if (!HANDWRITING_TOOL_ENABLED) {
    void releaseModelCache();
    return;
  }
  if (state.status !== "installed") return;
  getHandwritingRecognizer().warmup();
}

/** Cache Storage bucket transformers.js keeps the model files in. */
const MODEL_CACHE = "transformers-cache";

async function releaseModelCache(): Promise<void> {
  try {
    if (typeof caches === "undefined") return;
    await caches.delete(MODEL_CACHE);
  } catch {
    // Storage unavailable or blocked: nothing to release.
  }
}
